/**
 * Tiny "you're offline" / "back online" banner.
 *
 * Listens to the browser's online/offline events. When the connection
 * drops, slides in a thin amber strip at the top of the viewport so
 * the auditor knows their next action won't reach the server. When the
 * connection comes back, flashes a green confirmation for 2s, then
 * dismisses itself.
 *
 * The banner is positioned above everything (zIndex 4000) and is
 * RTL-aware (uses `inset-inline-start` so it lands on the right edge
 * of the screen in Hebrew). It's deliberately non-blocking: clicks
 * pass through to the underlying app.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';

export default function NetworkStatusBanner() {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [recentlyRecovered, setRecentlyRecovered] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setRecentlyRecovered(true);
      window.setTimeout(() => setRecentlyRecovered(false), 2000);
    };
    const handleOffline = () => {
      setOnline(false);
      setRecentlyRecovered(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (online && !recentlyRecovered) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 4000,
        bgcolor: online ? '#22C55E' : '#F59E0B',
        color: 'white',
        py: 0.75,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        fontSize: 13, fontWeight: 700,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        pointerEvents: 'none',
        animation: 'netslide 220ms ease-out',
        '@keyframes netslide': {
          from: { transform: 'translateY(-100%)' },
          to:   { transform: 'translateY(0)' },
        },
      }}
    >
      {online ? <WifiIcon fontSize="small" /> : <WifiOffIcon fontSize="small" />}
      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
        {online ? 'החיבור חזר' : 'אין חיבור לרשת — שינויים לא יישמרו עד שהחיבור יחזור'}
      </Typography>
    </Box>
  );
}
